import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { Icon } from './Icon';
import { colors, spacing, fontWeights, borderRadius } from '../theme';

export interface ConfirmDeleteModalProps {
  visible: boolean;
  title?: string;
  proposalName?: string;
  customerName?: string;
  message?: string;
  onClose: () => void;
  onConfirm: () => Promise<void> | void;
  confirmText?: string;
  cancelText?: string;
  loading?: boolean;
}

export const ConfirmDeleteModal: React.FC<ConfirmDeleteModalProps> = ({
  visible,
  title = 'Xóa tài liệu minh họa',
  proposalName,
  customerName,
  message,
  onClose,
  onConfirm,
  confirmText = 'Xóa tài liệu',
  cancelText = 'Hủy',
  loading = false,
}) => {
  if (!visible) return null;

  return (
    <View style={styles.backdrop}>
      <TouchableOpacity
        style={StyleSheet.absoluteFill}
        activeOpacity={1}
        onPress={onClose}
      />
      <View style={styles.card}>
        {/* Header Icon & Close button */}
        <View style={styles.header}>
          <View style={styles.iconCircle}>
            <Icon name="delete" size={24} color="#DC2626" />
          </View>
          <TouchableOpacity onPress={onClose} style={styles.closeBtn}>
            <Icon name="close" size={20} color={colors.onSurfaceVariant} />
          </TouchableOpacity>
        </View>

        {/* Content */}
        <View style={styles.body}>
          <Text style={styles.title}>{title}</Text>

          {proposalName || customerName ? (
            <View style={styles.infoCard}>
              {customerName ? (
                <View style={styles.infoRow}>
                  <Text style={styles.infoLabel}>Khách hàng:</Text>
                  <Text style={styles.infoValue}>{customerName}</Text>
                </View>
              ) : null}
              {proposalName ? (
                <View style={styles.infoRow}>
                  <Text style={styles.infoLabel}>Đề xuất:</Text>
                  <Text style={styles.infoValue}>{proposalName}</Text>
                </View>
              ) : null}
            </View>
          ) : null}

          <Text style={styles.messageText}>
            {message || 'Bạn có chắc chắn muốn xóa bản minh họa này? Hành động này không thể hoàn tác.'}
          </Text>
        </View>

        {/* Footer Actions */}
        <View style={styles.footer}>
          <TouchableOpacity
            style={styles.cancelBtn}
            onPress={onClose}
            disabled={loading}
          >
            <Text style={styles.cancelBtnText}>{cancelText}</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.confirmBtn, loading && styles.disabledBtn]}
            onPress={onConfirm}
            disabled={loading}
          >
            <Icon name="delete" size={16} color="#ffffff" style={{ marginRight: 6 }} />
            <Text style={styles.confirmBtnText}>
              {loading ? 'Đang xóa...' : confirmText}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing[4],
    zIndex: 2000,
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    width: '100%',
    maxWidth: 440,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.25,
    shadowRadius: 20,
    elevation: 10,
    padding: spacing[5],
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing[3],
  },
  iconCircle: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#FEE2E2',
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeBtn: {
    padding: spacing[1],
  },
  body: {
    marginBottom: spacing[5],
  },
  title: {
    fontSize: 18,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
    marginBottom: spacing[2],
  },
  infoCard: {
    backgroundColor: '#F9FAFB',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: borderRadius.DEFAULT,
    padding: spacing[3],
    marginVertical: spacing[2],
    gap: spacing[1.5],
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[2],
  },
  infoLabel: {
    fontSize: 12,
    color: colors.onSurfaceVariant,
    width: 80,
  },
  infoValue: {
    flex: 1,
    fontSize: 13,
    fontWeight: fontWeights.semibold,
    color: colors.onSurface,
  },
  messageText: {
    fontSize: 13,
    color: colors.onSurfaceVariant,
    lineHeight: 18,
    marginTop: spacing[2],
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: spacing[3],
  },
  cancelBtn: {
    paddingHorizontal: spacing[4],
    paddingVertical: spacing[2.5],
    borderRadius: borderRadius.DEFAULT,
    borderWidth: 1,
    borderColor: colors.outline,
    backgroundColor: '#ffffff',
    minWidth: 90,
    alignItems: 'center',
  },
  cancelBtnText: {
    fontSize: 13,
    fontWeight: fontWeights.semibold,
    color: colors.onSurfaceVariant,
  },
  confirmBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing[4],
    paddingVertical: spacing[2.5],
    borderRadius: borderRadius.DEFAULT,
    backgroundColor: '#DC2626',
    minWidth: 110,
  },
  confirmBtnText: {
    fontSize: 13,
    fontWeight: fontWeights.bold,
    color: '#ffffff',
  },
  disabledBtn: {
    opacity: 0.6,
  },
});
